import {
    Button,
    Flex,
    FormControl,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    FormErrorMessage,
    Box,
    Text,
    VStack,
    HStack,
} from "@chakra-ui/react";
import { IconDownload } from "@tabler/icons-react";
import { FunctionComponent, useState } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { downloadEntityTemplateAsync, Entity } from "../../../api/entities/entityApi";
import { FileContainer, FileUploader, useToast } from "am-tax-fe-core";
import { ALLOWED_BULK_UPLOAD_EXTENSIONS } from "../../../utils/constants";
import { useBulkUploadEntity } from "../../../api/entities/entityQueries";

interface EntityBulkUploadModalProps {
    show: boolean;
    onClose: () => void;
    clientId: string;
    onSave?: (data: Entity) => void;
}

type FormFields = {
    entities: File[];
};

export const EntityBulkUploadModal: FunctionComponent<EntityBulkUploadModalProps> = ({ show, onClose, clientId }) => {
    const formValues: FormFields = {
        entities: [],
    };

    const {
        handleSubmit,
        register,
        formState: { errors },
        setError,
        clearErrors,
    } = useForm<FormFields>({ values: formValues });

    const [selectedFile, setSelectedFile] = useState<FileContainer | undefined>();

    const { mutateAsync: bulkUploadMutation } = useBulkUploadEntity();

    const toast = useToast();

    // TODO: Add error handling for all of these mutations
    const onSubmit: SubmitHandler<FormFields> = async () => {
        if (!selectedFile) {
            setError("entities", {
                type: "manual",
                message: "Please upload a valid Excel file.",
            });
        } else {
            await bulkUploadMutation({ file: selectedFile!, clientId: clientId });
            onClose();
        }
    };

    const onFileAdded = async (fileContainer: FileContainer) => {
        const fileName = fileContainer.file.name;
        const fileExtension = fileName.split(".").pop()?.toLowerCase() || "";

        if (ALLOWED_BULK_UPLOAD_EXTENSIONS.includes(fileExtension)) {
            setSelectedFile(fileContainer);
            clearErrors("entities");
        } else {
            setSelectedFile(undefined);
            setError("entities", {
                type: "manual",
                message: "Invalid file format. Please upload an Excel file.",
            });
        }
    };

    async function downloadTemplate() {
        const response = await downloadEntityTemplateAsync();

        // Ensure the blob is treated as xlsm
        const blob = await response.blob();
        const contentType = response.headers.get("content-type") || "application/vnd.ms-excel.sheet.macroEnabled.12";
        const xlsmBlob = new Blob([blob], { type: contentType });

        // Get the filename from the response headers or use a default
        const contentDisposition = response.headers.get("content-disposition");
        const filenameMatch = contentDisposition?.match(/filename="(.+)"/);
        const filename = filenameMatch?.[1] || `Entity_Template.xlsx`;

        // Create blob and download
        const url = window.URL.createObjectURL(xlsmBlob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename.endsWith(".xlsx") ? filename : `${filename}.xlsx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);

        toast({
            title: "Download Complete",
            description: `Entity template has been downloaded`,
            status: "success",
            duration: 3000,
            isClosable: true,
        });
    }

    return (
        <Modal isOpen={show} onClose={onClose} size="xl">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>Bulk Upload Entities</ModalHeader>
                <ModalCloseButton />
                <form onSubmit={handleSubmit(onSubmit)}>
                    <ModalBody>
                        <VStack spacing={6} align="stretch">
                            {/* Step 1: Download Template */}
                            <Box>
                                <HStack align="center" mb={3}>
                                    <Box
                                        bg="blue.500"
                                        color="white"
                                        borderRadius="full"
                                        w={6}
                                        h={6}
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                        fontSize="sm"
                                        fontWeight="bold"
                                    >
                                        1
                                    </Box>
                                    <Text fontWeight="semibold">Download the Excel Template</Text>
                                </HStack>
                                <Box ml={6}>
                                    <Button
                                        leftIcon={<IconDownload />}
                                        variant="solid"
                                        colorScheme="green"
                                        size="md"
                                        onClick={() => {
                                            downloadTemplate();
                                        }}
                                    >
                                        Download Excel Template
                                    </Button>
                                </Box>
                            </Box>

                            {/* Step 2: Update Instructions */}
                            <Box>
                                <HStack align="center" mb={3}>
                                    <Box
                                        bg="blue.500"
                                        color="white"
                                        borderRadius="full"
                                        w={6}
                                        h={6}
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                        fontSize="sm"
                                        fontWeight="bold"
                                    >
                                        2
                                    </Box>
                                    <Text fontWeight="semibold">Update the excel file with your new entities</Text>
                                </HStack>
                            </Box>

                            {/* Step 3: Upload File */}
                            <Box>
                                <HStack align="center" mb={3}>
                                    <Box
                                        bg="blue.500"
                                        color="white"
                                        borderRadius="full"
                                        w={6}
                                        h={6}
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                        fontSize="sm"
                                        fontWeight="bold"
                                    >
                                        3
                                    </Box>
                                    <Text fontWeight="semibold">Upload the file:</Text>
                                </HStack>
                                <Box ml={6}>
                                    <FormControl isInvalid={!!errors?.entities}>
                                        <FileUploader
                                            {...register("entities")}
                                            maxFiles={1}
                                            onFileAdded={onFileAdded}
                                            onFileCancelled={() => setSelectedFile(undefined)}
                                            accept={{
                                                ".xlsx": [],
                                            }}
                                        />
                                        <Text fontSize="sm" mt={2}>
                                            Accepted file types: xlsx • Max file size: 25 MB
                                        </Text>
                                        <FormErrorMessage>{errors.entities?.message}</FormErrorMessage>
                                    </FormControl>
                                </Box>
                            </Box>
                        </VStack>
                    </ModalBody>
                    <ModalFooter>
                        <Flex w="100%" justify="right" align="center">
                            <Flex gap={2}>
                                <Button onClick={onClose} mr={3} variant="ghost">
                                    Cancel
                                </Button>
                                <Button type="submit" variant="primary" disabled={!selectedFile}>
                                    Upload
                                </Button>
                            </Flex>
                        </Flex>
                    </ModalFooter>
                </form>
            </ModalContent>
        </Modal>
    );
};
